import { Modal, ModalSizeEnum, NeutralButton, Popover, centsToUnits } from '@systemeio/ui'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import Badge from 'shared/components/badge'
import { Loader } from 'shared/components/loader'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { getQuery } from 'shared/route-query/utils/get-query'
import { currencyFormat } from 'shared/utils/currency-format'
import { dateFormat } from 'shared/utils/date-time-format'
import {
  InvoiceErrorStatusEnum,
  invoiceErrorStatusLocoKeys,
} from '../enum/invoice-error-status-enum'
import { InvoiceStateEnum, InvoiceStateTitle } from '../enums/invoice-state-enum'
import { InvoiceWithdrawalMethodEnum } from '../enums/invoice-withdrawal-method-enum'
import { useInvoiceDetails } from '../hooks/use-invoice-details'
import { InfoIcon } from '@systemeio/ui/icons'

interface InvoiceDetailsInterfaceProps {
  invoiceId: number | null
  onCloseModal: () => void
}

const renderWithdrawalDetails = (details: { [key: string]: string }) => {
  return Object.entries(details).map(([key, value]) => {
    const item = `${key}: ${value}`
    return <span key={item}>{item}</span>
  })
}

export const InvoiceDetailsModal = ({ invoiceId, onCloseModal }: InvoiceDetailsInterfaceProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const router = useRouter()
  const purchaseInvoiceId = router.query.purchaseInvoiceId as string

  const [visibleErrors, setVisibleErrors] = useState(3)

  const { invoiceDetails } = useInvoiceDetails({ invoiceId })

  const { unrelatedQuery } = getQuery(router.query, ['purchaseInvoiceId'])

  const onClose = () => {
    onCloseModal()
    setVisibleErrors(3)
    if (purchaseInvoiceId) {
      router.replace('/affiliate-dashboard', { query: unrelatedQuery }, { shallow: true })
    }
  }

  const loadMore = () => {
    setVisibleErrors(prevState => prevState + 3)
  }

  const showButton =
    invoiceDetails &&
    invoiceDetails.errors.length > 3 &&
    invoiceDetails.errors.length >= visibleErrors

  return (
    <Modal
      opened={!!invoiceId}
      size={ModalSizeEnum.medium}
      onClose={() => onClose()}
      title={t('dashboard.affiliate.state.invoice_details.modal_title')}
    >
      {invoiceDetails ? (
        <>
          <div className={'mb-8 flex gap-3'}>
            <div className={'flex grow flex-col gap-3'}>
              <span className={'text-base'}>{t('global.name')}</span>
              <span className={'text-text-primary'}>
                {invoiceDetails.purchaseInvoice.vendor.firstName}{' '}
                {invoiceDetails.purchaseInvoice.vendor.lastName}
              </span>
            </div>
            <div className={'flex grow flex-col gap-3'}>
              <span className={'text-base'}>
                {t('dashboard.affiliate.state.invoice_details.amount')}
              </span>
              <span className={'text-text-primary'}>
                {currencyFormat(
                  centsToUnits(invoiceDetails.purchaseInvoice.amount),
                  invoiceDetails.purchaseInvoice.currency,
                  user?.dashboardLocale,
                )}
              </span>
            </div>
            <div className={'flex grow flex-col gap-3'}>
              <span className={'text-base'}>
                {t('dashboard.affiliate.state.invoice_details.invoice_number')}
              </span>
              <span className={'text-text-primary'}>#{invoiceDetails.purchaseInvoice.number}</span>
            </div>
            <div className={'flex grow flex-col gap-3'}>
              <span className={'text-base'}>
                {t('dashboard.affiliate.state.invoice_details.invoice_status')}
              </span>
              <span className={'text-text-primary'}>
                {t(InvoiceStateTitle[InvoiceStateEnum.unpaid])}
              </span>
            </div>
            <div className={'flex grow flex-col gap-3'}>
              <span className={'text-base'}>{t('global.date')}</span>
              <span className={'text-text-primary'}>
                {dateFormat(invoiceDetails.purchaseInvoice.date, {
                  dateFormat: user?.dateFormat,
                  timeZone: user?.timezone,
                })}
              </span>
            </div>
          </div>
          {!!invoiceDetails.errors.length && (
            <div className={'mb-6'}>
              <h3 className={'mb-4 text-[20px]'}>
                {t('dashboard.affiliate.state.invoice_details.history_title')}
              </h3>
              <div className={'customScroll max-h-[250px] overflow-y-auto md:max-h-[400px]'}>
                {invoiceDetails.errors.slice(0, visibleErrors).map((error, index) => (
                  <div
                    key={`${error.date}-${index}`}
                    className={
                      'border-background-tertiary shadow-xs mb-3 flex rounded-lg border p-3'
                    }
                  >
                    <div className={'mt-1 flex h-7 flex-[25_1_0%]'}>
                      <Badge
                        text={t(invoiceErrorStatusLocoKeys[error.status])}
                        classNames={`text-background-secondary text-base ${
                          error.status === InvoiceErrorStatusEnum.Failed
                            ? 'bg-danger'
                            : 'bg-text-secondary/80'
                        }`}
                      />
                    </div>
                    <div className={'flex flex-[75_1_0%] flex-col gap-2'}>
                      <p className={'text-text-primary flex gap-1 text-base'}>
                        {error.errorMessage ? (
                          <TransLoco
                            t={t}
                            i18nKey="dashboard.affiliate.state.invoice_details.default_error_bank"
                            values={{
                              withdrawalMethod: t(
                                InvoiceWithdrawalMethodEnum[error.withdrawalMethod],
                              ),
                              errorMessage: error.errorMessage,
                            }}
                            components={{ b: <b /> }}
                          />
                        ) : (
                          t('dashboard.affiliate.state.invoice_details.default_error_massage')
                        )}
                        {error.errorMessageOriginal && (
                          <Popover
                            label={error.errorMessageOriginal}
                            popoverClassName={'w-[250px] sm:w-[450px]'}
                          >
                            {show => (
                              <InfoIcon
                                className={`${
                                  show ? 'fill-brand-primary' : 'fill-fill-primary'
                                } h-[18px] w-[18px]`}
                              />
                            )}
                          </Popover>
                        )}
                      </p>
                      <div className={'flex gap-3'}>
                        <div className={'flex gap-1'}>
                          <span className={'text-brand-primary'}>
                            {t(InvoiceWithdrawalMethodEnum[error.withdrawalMethod])}
                          </span>
                          <Popover
                            label={
                              <div className={'flex flex-col items-start gap-2'}>
                                {renderWithdrawalDetails(error.withdrawalDetails)}
                              </div>
                            }
                          >
                            {show => (
                              <InfoIcon
                                className={`${
                                  show ? 'fill-brand-primary' : 'fill-fill-primary'
                                } h-[18px] w-[18px]`}
                              />
                            )}
                          </Popover>
                        </div>
                        <span>
                          {dateFormat(error.date, {
                            dateFormat: user?.dateFormat,
                            timeZone: user?.timezone,
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {showButton && (
            <div className={'flex justify-center'}>
              <NeutralButton onClick={() => loadMore()}>
                {t('dashboard.affiliate.state.invoice_details.load_more')}
              </NeutralButton>
            </div>
          )}
        </>
      ) : (
        <div className="m-auto table">
          <Loader type="dark" />
        </div>
      )}
    </Modal>
  )
}
